import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

/**
 * Editorial cookie consent banner.
 *
 * Sits flush against the bottom-left, mirrored across from the
 * floating WhatsApp button so it never collides with it. The banner is
 * deliberately quiet — a cream card with a hairline gold rule — rather
 * than a full-width overlay, because nothing on this site asks for
 * non-essential cookies aggressively enough to justify a more
 * disruptive treatment.
 *
 * The choice is persisted to `localStorage` under
 * `hh:cookie-consent` and exposed globally via the
 * `window.__hhCookieConsent` event so any future analytics initialiser
 * can read it before booting.
 *
 * Returning to the banner is possible by clearing the stored value;
 * the legal pages link here implicitly via the "cookie preferences" 
 * language in /privacy.
 */

type Consent = "accepted" | "essential-only";

const STORAGE_KEY = "hh:cookie-consent";

interface ConsentRecord {
  status: Consent;
  decidedAt: string;
  version: 1;
}

function readConsent(): ConsentRecord | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentRecord;
    if (parsed.version !== 1) return null;
    if (parsed.status !== "accepted" && parsed.status !== "essential-only") {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeConsent(status: Consent): void {
  if (typeof window === "undefined") return;
  const record: ConsentRecord = {
    status,
    decidedAt: new Date().toISOString(),
    version: 1,
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // localStorage may be disabled in private mode — the banner will
    // simply re-appear next time, which is the safest fallback.
  }
  window.dispatchEvent(
    new CustomEvent("hh:cookie-consent", { detail: record }),
  );
}

export function CookieBanner() {
  const [visible, setVisible] = useState(false);
  // Wait one frame after mount before showing the banner so it doesn't
  // race with above-the-fold rendering (and so SSR / first-paint stays
  // clean if we ever introduce SSR).
  useEffect(() => {
    if (readConsent() !== null) return;
    const id = window.requestAnimationFrame(() => setVisible(true));
    return () => window.cancelAnimationFrame(id);
  }, []);

  if (!visible) return null;

  const decide = (status: Consent) => {
    writeConsent(status);
    setVisible(false);
  };

  return (
    <div
      role="region"
      aria-label="Cookie preferences"
      className="pointer-events-none fixed inset-x-0 bottom-[var(--compare-bar-h)] z-50 flex justify-start p-4 pb-[calc(1rem+var(--safe-bottom))] sm:p-6 sm:pb-[calc(1.5rem+var(--safe-bottom))]"
    >
      <div
        className="pointer-events-auto relative w-full max-w-[420px] border border-[var(--line-strong)] bg-[var(--surface)] p-5 shadow-[0_18px_48px_-16px_rgba(20,30,48,0.18)] sm:p-6"
      >
        <span
          aria-hidden
          className="absolute left-0 right-0 top-0 h-px bg-[var(--gold-bright)] opacity-70"
        />
        <p className="eyebrow text-[var(--gold-bright)]">A quick note</p>
        <h2
          className="mt-3 font-display text-[20px] leading-[1.2] text-[var(--text)] sm:text-[22px]"
          style={{ fontVariationSettings: '"opsz" 36' }}
        >
          We use a small number of cookies.
        </h2>
        <p className="mt-3 text-[13.5px] leading-[1.65] text-[var(--text-soft)]">
          Essential cookies keep your shortlist, compare tray, and form
          progress working. Optional analytics help us understand which
          communities and articles are useful — only with your consent.{" "}
          <Link
            to="/privacy#cookies"
            className="underline decoration-[var(--line-strong)] underline-offset-[3px] hover:text-[var(--gold-bright)] hover:decoration-[var(--gold-bright)]"
          >
            Read our cookie note
          </Link>
          .
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-2.5">
          <button
            type="button"
            onClick={() => decide("accepted")}
            className="btn btn-primary btn-block-sm"
          >
            Accept all
          </button>
          <button
            type="button"
            onClick={() => decide("essential-only")}
            className="btn btn-ghost btn-block-sm"
          >
            Essential only
          </button>
        </div>
      </div>
    </div>
  );
}
